import React from 'react';
import Icon from '@/shared/components/Icon';

interface TestPanelSectionProps {
    title: string;
    biomarkerCount: number;
    lastTestDate?: string;
    children: React.ReactNode;
    className?: string;
    /** Optional node rendered below the panel info in the left sidebar.
     *  Used to tuck the provider clinical-context controls into otherwise
     *  empty space on the first panel of an order. */
    sidebarSlot?: React.ReactNode;
}

const TestPanelSection: React.FC<TestPanelSectionProps> = ({
    title,
    biomarkerCount,
    lastTestDate,
    children,
    className = '',
    sidebarSlot,
}) => {
    return (
        <div className={`mx-3 lg:mx-6 ${className}`}>
            {/* Mobile: Stacked layout */}
            <div className="lg:hidden">
                {/* Panel Info on mobile */}
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-mld-text-primary mb-1">{title}</h2>
                    <div className="text-sm text-gray-500 space-y-1">
                        <div>{biomarkerCount} Biomarkers</div>
                        {lastTestDate && (
                            <div className="flex items-center gap-1">
                                <Icon name="calendar" className="h-3 w-3" />
                                <span>
                                    Last Tested{' '}
                                    {new Date(lastTestDate).toLocaleDateString('en-US', {
                                        month: 'numeric',
                                        day: 'numeric',
                                        year: '2-digit',
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                    {sidebarSlot ? <div className="mt-4">{sidebarSlot}</div> : null}
                </div>

                {/* Biomarker Cards on mobile */}
                <div className="bg-white rounded-2xl shadow-sm border border-mld-panel-border p-4">
                    <div className="space-y-3">{children}</div>
                </div>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:flex gap-8">
                {/* Left Column - Panel Info (on page background) */}
                <div className="w-72 flex-shrink-0">
                    <h2 className="text-xl font-semibold text-mld-text-primary mb-1">{title}</h2>
                    <div className="text-sm text-gray-500 space-y-1">
                        <div>{biomarkerCount} Biomarkers</div>
                        {lastTestDate && (
                            <div className="flex items-center gap-1">
                                <Icon name="calendar" className="h-3 w-3" />
                                <span>
                                    Last Tested{' '}
                                    {new Date(lastTestDate).toLocaleDateString('en-US', {
                                        month: 'numeric',
                                        day: 'numeric',
                                        year: '2-digit',
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                    {sidebarSlot ? <div className="mt-5">{sidebarSlot}</div> : null}
                </div>

                {/* Right Column - Biomarker Cards in rounded panel */}
                <div className="flex-1">
                    <div className="bg-white rounded-2xl shadow-sm border border-mld-panel-border p-6">
                        <div className="space-y-3">{children}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(TestPanelSection);
